// Media Center — content constants.
// One source of truth for every string, list, and asset URL used by
// the /media page. Keeping the copy here (instead of scattered across
// components) makes it trivial for the DJ SPY team to add a press
// article, swap a venue photo, or reorder the timeline without
// touching JSX. Every URL is stable (customer-assets.emergentagent.net).

const ASSET = "https://customer-assets-lqy194kg.emergentagent.net/job_smooth-animation-hub-3/artifacts";
const ASSET_LEGACY = "https://customer-assets.emergentagent.com/job_smooth-animation-hub-3/artifacts";

export const PHOTOS = {
  // The throne portrait — full-body, direct gaze, editorial lighting.
  // This is the Forbes-feature-magazine shot and headlines the page.
  heroPortrait:     `${ASSET}/m1mwk51x_800eb07e-f7f4-4a04-8849-503b075d450f.JPG`,
  // Cross-armed on the throne — the Vision-section feature portrait.
  visionPortrait:   `${ASSET}/v0gvpc4d_7616beac-82ea-4101-8726-0f69611b9ceb.JPG`,
  // Bio-section portrait — high-energy performance moment.
  bioPortrait:      `${ASSET}/hx0ng6o6_dc11166c-f39a-4182-bce2-225915dac6fa.JPG`,
  // Portrait with headphones, thoughtful — used in Music section.
  studioPortrait:   `${ASSET}/tzg70hsf_IMG_2504.PNG`,
  // Editorial DJ-booth profile with orange rim-light.
  boothPortrait:    `${ASSET}/zumd9zgk_IMG_2801.PNG`,
  // Newest performance shot — DJ with mic, colourful backdrop.
  performanceMic:   `${ASSET}/wggp4mf7_IMG_2802.PNG`,
  // Legacy live shots kept for the gallery.
  performanceRed:   `${ASSET}/4u0kqb3z_AFBF9B69-EEC1-4427-AB71-B5E357AA185B.jpeg`,
  performanceNeon:  `${ASSET}/pmhw31aw_2BA609BC-7B0C-49ED-9D60-896F8ED8F0D9.jpeg`,
  performanceOrange:`${ASSET}/y0m5wzeo_9A9FDEEB-6359-434B-9D43-46591E3F0194.jpeg`,
  performanceGrid:  `${ASSET}/z5nrssmi_83429244-F4F3-45FF-B65A-00733047C91B.jpeg`,
  performanceMic2:  `${ASSET}/klajn9kx_5008d54e-775b-4c8b-acbc-df42b45570d4.JPG`,
  extra1:           `${ASSET_LEGACY}/4s8ayza7_IMG_0930.webp`,
  extra2:           `${ASSET_LEGACY}/hu4re5t2_IMG_0931.webp`,
  extra3:           `${ASSET_LEGACY}/zd48gv67_IMG_0932.webp`,
  behind1:          `${ASSET_LEGACY}/ejc5yk5v_IMG_2523.webp`,
  behind2:          `${ASSET_LEGACY}/qu5qjqol_IMG_2522.webp`,
  behind3:          `${ASSET_LEGACY}/bxsmdsjj_IMG_2521.webp`,
  logoDark:         `${ASSET_LEGACY}/wu834haa_SPY%20WHITE.png`,
  logoMark:         `${ASSET_LEGACY}/dw6nb8eh_DJ%20SPY%20LOGO%20small.JPG`,
};

// Gallery categories — organised for lightbox display.
export const GALLERY = [
  { category: "Editorial Portraits", items: [
      { src: `${ASSET}/m1mwk51x_800eb07e-f7f4-4a04-8849-503b075d450f.JPG`, alt: "DJ SPY editorial throne portrait" },
      { src: `${ASSET}/v0gvpc4d_7616beac-82ea-4101-8726-0f69611b9ceb.JPG`, alt: "DJ SPY cross-armed portrait" },
      { src: `${ASSET}/tzg70hsf_IMG_2504.PNG`,                             alt: "DJ SPY with headphones" },
      { src: `${ASSET}/4u0kqb3z_AFBF9B69-EEC1-4427-AB71-B5E357AA185B.jpeg`, alt: "DJ SPY red-neon portrait" },
    ]
  },
  { category: "Live Performances", items: [
      { src: `${ASSET}/hx0ng6o6_dc11166c-f39a-4182-bce2-225915dac6fa.JPG`, alt: "DJ SPY live with mic" },
      { src: `${ASSET}/wggp4mf7_IMG_2802.PNG`,                             alt: "DJ SPY on stage" },
      { src: `${ASSET}/zumd9zgk_IMG_2801.PNG`,                             alt: "DJ SPY at CDJs" },
      { src: `${ASSET}/y0m5wzeo_9A9FDEEB-6359-434B-9D43-46591E3F0194.jpeg`, alt: "DJ SPY orange profile" },
      { src: `${ASSET}/pmhw31aw_2BA609BC-7B0C-49ED-9D60-896F8ED8F0D9.jpeg`, alt: "DJ SPY neon cube stage" },
      { src: `${ASSET}/klajn9kx_5008d54e-775b-4c8b-acbc-df42b45570d4.JPG`, alt: "DJ SPY performing with balloons" },
    ]
  },
  { category: "Behind The Booth", items: [
      { src: `${ASSET}/z5nrssmi_83429244-F4F3-45FF-B65A-00733047C91B.jpeg`, alt: "DJ SPY behind booth" },
    ]
  },
  { category: "Festival Moments", items: [
      { src: `${ASSET_LEGACY}/4s8ayza7_IMG_0930.webp`, alt: "Festival stage" },
      { src: `${ASSET_LEGACY}/hu4re5t2_IMG_0931.webp`, alt: "Crowd shot" },
      { src: `${ASSET_LEGACY}/zd48gv67_IMG_0932.webp`, alt: "Festival lights" },
    ]
  },
];

// International Presence — venues + countries are sourced live from
// the Trusted-By admin panel (`GET /api/trusted-by`). Any changes the
// team makes there — add / edit / hide / reorder — flow into
// `/media` automatically without a redeploy.
// The country column is derived in `<InternationalPresence />` by
// grouping the fetched venues by their `country` field.

// Only two dates are independently confirmed by the artist: 1999
// (career start) and the launch of DJSPYOfficial.com in 2024.
// Everything in between uses **period labels** — never invented years.
export const TIMELINE = [
  { year: "1999",    title: "Career Begins",                          copy: "Johnny Eid begins performing under the DJ SPY name in Lebanon's underground scene." },
  { year: "—",       title: "Regional Performances",                  copy: "Bookings expand across the Middle East and North Africa." },
  { year: "—",       title: "Major Club Residencies",                 copy: "Flagship venues in Dubai, Amman, Doha, Giza, and Manama." },
  { year: "—",       title: "Same-Stage Festival Appearances",        copy: "Performs on the same stage as internationally recognized Arab artists during festivals, concerts, and major live events." },
  { year: "—",       title: "Original Productions & Exclusive Edits", copy: "DJ SPY begins releasing original tracks, remixes, and DJ-tool edits." },
  { year: "2024",    title: "Launch of DJSPYOfficial.com",            copy: "Launch of the official DJ SPY digital storefront for DJ-ready Arabic club music." },
  { year: "2024 —",  title: "Digital Platform Growth",                copy: "Full music library, membership tier, editorial blog, and professional-quality DJ edits." },
];

export const TIMELINE_DISCLAIMER =
  "Timeline entries reflect confirmed career milestones. Undated period labels indicate ongoing phases rather than specific years.";

export const ALONGSIDE = [
  "Cheb Khaled", "Mohamed Ramadan", "Myriam Fares",
  "Ali Saber",   "Saif Nabil",       "Saad Lamjarred",
];
export const ALONGSIDE_DISCLAIMER =
  "DJ SPY has performed on the same stage as internationally recognized Arab artists during festivals, concerts, and major live events.";

export const GENRES = [
  "Festival EDM", "Afro House", "Melodic Techno",
  "Arabic Club Music", "Moombahton", "Reggaeton",
];

export const RECOGNITION = [
  { title: "YouTube Silver Creator Award", copy: "100K+ subscriber milestone officially awarded by YouTube." },
  { title: "100K+ YouTube Community",      copy: "A growing audience of DJs, dancers, and Arabic-club music fans." },
  { title: "2M+ Anghami Streams",          copy: "Cumulative streams across original productions and exclusive edits." },
  { title: "600+ Live Performances",       copy: "Two decades of touring across the Middle East and North Africa." },
  { title: "20+ Years Of Experience",      copy: "Continuously performing and producing since 1999." },
  { title: "Professional Music Platform",  copy: "Founder of DJSPYOfficial.com — a professional library built for DJs." },
];

// Only surface platforms whose URLs are personally confirmed by DJ SPY.
// Add-back list (Spotify / Apple Music / Anghami / Snapchat / YouTube)
// is intentionally omitted here until an official URL is supplied.
export const PLATFORMS = [
  { name: "Website",   handle: "djspyofficial.com", url: "https://www.djspyofficial.com" },
  { name: "Instagram", handle: "@dj_spy",           url: "https://instagram.com/dj_spy" },
  { name: "TikTok",    handle: "@djspy666",         url: "https://tiktok.com/@djspy666" },
  { name: "Telegram",  handle: "@dj_spy",           url: "https://t.me/dj_spy" },
];

// Direct Channels rendered beside the International Bookings form.
// Sourced from the same verified_presence records — this fallback
// only kicks in when the CMS payload is missing/unhealthy. When the
// CMS is live, the adapter emits `BOOKING_CHANNELS` filtered by the
// per-item `show_in_bookings` toggle so admins can hide a channel
// from bookings without removing it from Verified Presence.
export const BOOKING_CHANNELS = PLATFORMS;

export const PRESS_ARTICLES = [
  { publication: "UAE Stories",        headline: "Johnny Eid: The Beatmaker Bridging Cultures",   url: "https://uaestories.com/johnny-eid-the-beatmaker-bridging-cultures/",           date: "2024" },
  { publication: "Gulf Magazine",      headline: "Johnny Eid – The Journey of DJ SPY",             url: "https://gulfmagazine.co/johnny-eid-the-journey-of-dj-spy/",                    date: "2024" },
  { publication: "Shams Al Youm",      headline: "الدي جي جوني عيد المعروف بـ DJ SPY ذوق فريد",     url: "https://shams-alyaoum.com/%d8%a7%d9%84%d8%af%d9%8a-%d8%ac%d9%8a-%d8%ac%d9%88%d9%86%d9%8a-%d8%b9%d9%8a%d8%af-%d8%a7%d9%84%d9%85%d8%b9%d8%b1%d9%88%d9%81-%d8%a8%d9%80dj-spy-%d8%b0%d9%88%d9%82-%d9%81%d8%b1%d9%8a/", date: "2024" },
  { publication: "Al Khabr Elyoum",    headline: "حوار مع الدي جي جوني عيد",                       url: "https://alkhabrelyoum.com/15071",                                             date: "2024" },
  { publication: "Masr Sa3a",          headline: "قصة نجاح جوني عيد",                              url: "https://masrsa3a.com/?p=55146",                                               date: "2024" },
  { publication: "Asiaa Press",        headline: "DJ SPY يبني منصة رقمية للـ DJs",                 url: "https://www.asiaa-press.com/?p=1020169",                                      date: "2024" },
  { publication: "NBD Al Asima",       headline: "الدي جي جوني عيد في حديث جديد",                  url: "https://nbdaleaisma.com/2026/01/16/70442",                                    date: "2026" },
  { publication: "Mejalet El Fann",    headline: "الدي جي جوني عيد المعروف بـ DJ SPY",             url: "https://mejaletelfn.com/%d8%a7%d9%84%d8%af%d9%8a-%d8%ac%d9%8a-%d8%ac%d9%88%d9%86%d9%8a-%d8%b9%d9%8a%d8%af-%d8%a7%d9%84%d9%85%d8%b9%d8%b1%d9%88%d9%81-%d8%a8%d9%80dj-spy-%d8%b0%d9%88%d9%82-%d9%81%d8%b1%d9%8a/", date: "2024" },
  { publication: "El Wathiqa",         headline: "ملف عن الدي جي جوني عيد",                        url: "https://www.elwathiqa.com/5946",                                              date: "2024" },
  { publication: "Senators Egypt",     headline: "الدي جي جوني عيد – DJ SPY",                      url: "https://www.senatorsegy.com/tfr/3190",                                        date: "2024" },
];

// Only actual, downloadable assets are `available: true`. The Logo
// SVG intentionally stays out of this list — awaiting an approved
// vector master before it can be re-enabled.
export const DOWNLOADS = [
  { key: "bio_pdf",       label: "Official Biography (PDF)",  available: true,  href: "/api/media/download/biography.pdf",         filename: "dj-spy-official-biography.pdf",   note: "One-page editorial biography." },
  { key: "bio_docx",      label: "Official Biography (DOCX)", available: true,  href: "/api/media/download/biography.docx",        filename: "dj-spy-official-biography.docx",  note: "Editable Word version for magazine layouts." },
  { key: "press_kit",     label: "Official Press Kit (PDF)",  available: true,  href: "/api/media/download/press-kit.pdf",         filename: "dj-spy-official-press-kit.pdf",   note: "Six-page press kit with photos, stats, quotes." },
  { key: "fact_sheet",    label: "One-Page Fact Sheet (PDF)", available: true,  href: "/api/media/download/fact-sheet.pdf",        filename: "dj-spy-one-page-fact-sheet.pdf",  note: "Key numbers at a glance for editors." },
  { key: "brand_guides",  label: "Brand Guidelines (PDF)",    available: true,  href: "/api/media/download/brand-guidelines.pdf",  filename: "dj-spy-brand-guidelines.pdf",     note: "Logo usage, spacing, colours, typography." },
  { key: "press_photos",  label: "Press Photos (ZIP)",        available: true,  href: "/api/media/download/press-photos.zip",      filename: "dj-spy-press-photos.zip",         note: "High-resolution portraits + performance shots." },
  { key: "logo_png",      label: "DJ SPY Official Logo (PNG)",available: true,  href: PHOTOS.logoDark, filename: "dj-spy-official-logo.png", note: "Transparent white logo, high resolution." },
];

export const DOWNLOAD_USAGE_NOTICE =
  "Official DJ SPY media assets may be used by journalists, promoters and approved partners for editorial or event-promotion purposes. Images must not be materially altered, resold or used to imply endorsement without permission. Photo credit: DJ SPY Official.";

export const HERO_STATS = [
  { value: "20+",   label: "Years Experience" },
  { value: "600+",  label: "Live Performances" },
  { value: "2M+",   label: "Anghami Streams" },
  { value: "100K+", label: "YouTube Community" },
];

// Section 04 — By the Numbers. Independent from HERO_STATS so admins
// can curate them separately in the Media Center CMS.
export const NUMBERS = [
  { value: "20+",   label: "Years Experience",  copy: "" },
  { value: "600+",  label: "Live Performances", copy: "" },
  { value: "2M+",   label: "Anghami Streams",   copy: "" },
  { value: "100K+", label: "YouTube Community", copy: "" },
];

// Section 09 platform features — mirrors the UI_EN.s09_platform_features
// list. Extracted here as a fallback so admin deletions in the CMS
// (e.g. removing "Membership") aren't resurrected by hardcoded UI copy.
export const PLATFORM_FEATURES = [
  "Professional DJ Music Store",
  "Instant Downloads",
  "Exclusive Remixes",
  "Original Productions",
  "Membership",
  "Blog · SEO Articles",
  "Professional Music Library",
];

export const QUICK_FACTS = [
  { label: "Stage Name",     value: "DJ SPY" },
  { label: "Real Name",      value: "Johnny Eid" },
  { label: "Nationality",    value: "Lebanese" },
  { label: "Based",          value: "Dubai, UAE" },
  { label: "Career Started", value: "1999" },
  { label: "Languages",      value: "Arabic · English" },
];

export const BIO_PARAGRAPHS = [
  "Johnny Eid — performing under the name DJ SPY — is a Lebanese DJ, producer, and entrepreneur based in Dubai. With more than two decades behind the decks since 1999, he has built a signature sound in Arabic club music, moving seamlessly between festival EDM, Afro House, melodic techno, and modern Arabic productions.",
  "His performances have spanned flagship venues across the Middle East and North Africa — from Omni Dubai and Stage Dubai to Omni Amman, OX Doha, Opal Giza, and BD House Jeddah — sharing the same stages as internationally recognized Arab artists during festivals, concerts, and major live events.",
  "Beyond the booth, DJ SPY is building DJSPYOfficial.com — a professional digital platform dedicated to DJs worldwide, providing professional-quality DJ-ready Arabic club edits, remixes, mashups, and original productions.",
];

export const VISION_HEADLINE  = "Modernizing Arabic Club Music for DJs Worldwide";
export const VISION_PARAGRAPHS = [
  "For decades, Arabic club music has thrived inside a fragmented ecosystem — brilliant edits and remixes trapped in private WhatsApp groups, unlicensed downloads, and hard drives passed between DJs. DJ SPY is quietly rebuilding that infrastructure from scratch.",
  "Through DJSPYOfficial.com, he is bringing a professional standard to Arabic club music: professional-quality DJ-ready files, remixes and edits, transparent pricing, and instant delivery. The platform serves DJs across the Middle East, North Africa, and the global diaspora — offering the exclusive edits that make a set feel unmistakably DJ SPY.",
  "It is a bet on the professionalization of a scene that has always been culturally powerful but commercially informal. And it is the natural next chapter for an artist whose entire career has been spent bridging cultures — Lebanon to Dubai, Arabic to English, festival stage to production studio.",
];

export const STATS_DISCLAIMER =
  "Career figures reflect available records and platform milestones as of 2026, at the time of publication.";

export const BOOKING = {
  // Public-facing booking mailbox (also the notification recipient).
  // This is the address journalists and buyers see on the page.
  email:    "booking@djspyofficial.com",
  telegram: "https://t.me/dj_spy",
  instagram:"https://instagram.com/dj_spy",
  tiktok:   "https://tiktok.com/@djspy666",
  website:  "https://www.djspyofficial.com",
};
